// The viewpoint a plugin captured: put the viewer at it, and mark the elements
// it was about.
//
// Owner, 2026-10-21: "this has to work on viewpoints, and the
// elements/fragments in that view point + a screen shot need to be both sent
// to bidlight, so workers can rotate the 3d and see the picture on the view".
//
// A HOOK IN ITS OWN FILE, not eighty lines inside IssueModelCanvas. That
// component is already large enough that adding this much to it overflows
// eslint-plugin-react-hooks' code-path counter, which then reports "hook
// called conditionally" at an unrelated hook two hundred lines earlier and
// FAILS THE BUILD (see feedback: hooks lint path limit, 2026-09-19). Keeping
// the feature here costs the canvas three lines and keeps it testable.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { viewerCameraFrom, applyViewerCamera } from "helpers/viewpointCamera";
import { askForBranchGeometry, ROW_BUDGET, UID_CAP, TILE_BUDGET } from "helpers/lazyCategoryGeometry";

const HIGHLIGHT = "#2f6fed";

/** What to say about a restore attempt, if anything. */
export function viewpointNote(r) {
  if (!r) return "";
  if (r.applied) return r.approximated ? r.reason : "";
  return r.reason ? `The viewpoint could not be restored: ${r.reason}` : "";
}

// Tiles for one viewpoint, across all its asks. A viewpoint is a set somebody
// chose, not a category, so it gets more than one branch's TILE_BUDGET - but
// still a ceiling: 96 tiles is about 90 MB of fragments at BA03's sizes.
export const VIEWPOINT_TILE_BUDGET = TILE_BUDGET * 3;

/**
 * The linked elements that can be in a published commit at all. DWG entities
 * and other Navisworks items are not until they are published as documents of
 * their own (Navisworks design, part 2); asking for them can only miss.
 */
export function linkedCommitUids(issue) {
  const raw = issue && Array.isArray(issue.linkedElements) ? issue.linkedElements : [];
  const out = [];
  const seen = new Set();
  for (const e of raw) {
    if (!e || !e.uniqueId || seen.has(e.uniqueId)) continue;
    if ((e.source && e.source !== "revit") || /^(dwg|nw):/.test(e.uniqueId)) continue;
    seen.add(e.uniqueId);
    out.push(e.uniqueId);
  }
  return out;
}

/** Ask-sized pieces of a uid list, up to the viewer's exemption cap. */
export function chunkUids(uids, size = ROW_BUDGET, cap = UID_CAP) {
  const list = (uids || []).slice(0, cap);
  const out = [];
  for (let i = 0; i < list.length; i += size) out.push(list.slice(i, i + size));
  return out;
}

/** What to say about fetching the linked elements' geometry, if anything. */
export function linkedGeometryNote(t) {
  if (!t) return "";
  const parts = [];
  if (t.notInCommit > 0) {
    parts.push(`${t.notInCommit} of the viewpoint's ${t.asked} elements are not in this model version.`);
  }
  if (t.untiled > 0) parts.push(`${t.untiled} were exported without a tile, so their geometry cannot be fetched.`);
  if (t.skipped > 0) parts.push(`${t.skipped} are DWG or other Navisworks items, which are not published yet.`);
  if (t.truncated) parts.push("Not every tile was fetched up front - the rest load as the view reaches them.");
  if (t.failed) parts.push("The linked elements' geometry could not be fetched ahead - it loads as the view reaches it.");
  return parts.join(" ");
}

/** The isolate button's wording. */
export function viewpointIsolateTitle(count, isolated) {
  const many = count === 1 ? "element" : "elements";
  if (isolated) return `Show the rest of the model again (${count} ${many} from the viewpoint)`;
  return `Isolate the ${count} ${many} this viewpoint was about`;
}

/**
 * @param issue      the issue as loaded
 * @param isGLLoaded the model is up
 * @param cameraControls () => camera-controls instance or null
 * @param store      () => the viewer state store or null
 * @param pinnedView truthy when a human pinned a markup view that applies here
 */
export default function useViewpointCamera({
  issue, isGLLoaded, cameraControls, store, pinnedView, viewer, projectId,
}) {
  const [note, setNote] = useState("");
  const [geometryNote, setGeometryNote] = useState("");
  const fetched = useRef(false);
  const [isolated, setIsolated] = useState(false);
  const done = useRef(false);

  const camera = issue && issue.sourceModelViewer ? issue.sourceModelViewer.camera : null;

  // ONCE, on the first load. Re-running would fight the worker every time the
  // model finished a tile - the point is that they can rotate away from it.
  //
  // A PINNED MARKUP VIEW WINS: somebody composed that deliberately on this
  // issue, while the viewpoint camera came off a file. A deliberate act
  // outranks a derived one.
  useEffect(() => {
    if (!isGLLoaded || done.current) return;
    if (pinnedView) { done.current = true; return; }
    const view = viewerCameraFrom(camera);
    if (!view) return;
    const cc = cameraControls && cameraControls();
    if (!cc) return;
    done.current = true;
    setNote(viewpointNote(applyViewerCamera(cc, view)));
  }, [isGLLoaded, camera, pinnedView, cameraControls]);

  const uids = useMemo(() => {
    const raw = issue && Array.isArray(issue.linkedElements) ? issue.linkedElements : [];
    const seen = [];
    for (const e of raw) {
      if (e && e.uniqueId && !seen.includes(e.uniqueId)) seen.push(e.uniqueId);
    }
    return seen;
  }, [issue]);

  // THE LINKED ELEMENTS' GEOMETRY, WHEREVER THE CAMERA IS (Navisworks design,
  // ask C). Streaming loads what the camera sees, so a linked element behind
  // it, or too small for the LOD budget, was highlighted with nothing to show.
  // streaming.prefetchTiles alone does not fix that - it only re-orders the
  // queue and a boosted tile still has to pass the frustum gate - so this goes
  // through askForBranchGeometry, which also forces the elements to build
  // (streaming.ensureUids) and reports the ones the commit does not have.
  //
  // AGAINST THE PINNED PUSH when the issue has one: that is the manifest being
  // streamed, so its rows and its tiles agree. ONCE, like the camera.
  const commitUids = useMemo(() => linkedCommitUids(issue), [issue]);
  const pinnedCommit = issue && issue.sourceModel && issue.sourceModel.kind === "commit"
    ? String(issue.sourceModel.commitId || "") : "";
  useEffect(() => {
    if (!isGLLoaded || fetched.current || !projectId || !uids.length) return undefined;
    const v = viewer && viewer();
    if (!v || !v.streaming) return undefined;
    fetched.current = true;
    let alive = true;
    (async () => {
      const t = { asked: 0, notInCommit: 0, untiled: 0, skipped: uids.length - commitUids.length, truncated: false, failed: false };
      let budget = VIEWPOINT_TILE_BUDGET;
      for (const chunk of chunkUids(commitUids)) {
        if (!alive) return;
        if (budget <= 0) { t.truncated = true; break; }
        // One ask at a time keeps the tile budget honest.
        const r = await askForBranchGeometry({
          projectId, commit: pinnedCommit || undefined, uids: chunk, viewer: v, tileBudget: Math.min(TILE_BUDGET, budget),
        });
        t.asked += chunk.length;
        if (r.error) { t.failed = true; continue; }
        budget -= (r.tiles || []).length;
        t.notInCommit += r.notInCommit || 0;
        t.untiled += (r.untiledUids || []).length;
        if (r.truncated || r.uidsTruncated) t.truncated = true;
      }
      if (commitUids.length > UID_CAP) t.truncated = true;
      if (alive) setGeometryNote(linkedGeometryNote(t));
    })();
    return () => { alive = false; };
  }, [isGLLoaded, projectId, uids, commitUids, pinnedCommit, viewer]);

  // HIGHLIGHTED, NOT ISOLATED. Isolating on open hides the context that makes
  // a clash legible; the worker isolates from the button when they want only
  // those elements.
  useEffect(() => {
    if (!isGLLoaded || !uids.length) return undefined;
    const st = store && store();
    if (!st || !st.bulkHighlight) return undefined;
    try {
      st.bulkHighlight(uids.map((u) => [u, HIGHLIGHT]));
    } catch (e) { /* a mark is never worth the page */ }
    return () => {
      try {
        if (st.bulkClearHighlights) st.bulkClearHighlights(uids);
      } catch (e) { /* unmounting */ }
    };
  }, [isGLLoaded, uids, store]);

  const toggleIsolation = useCallback(() => {
    const st = store && store();
    if (!st || !st.setIsolation) return;
    try {
      if (isolated) { st.setIsolation(null); setIsolated(false); return; }
      st.setIsolation(uids);
      setIsolated(true);
    } catch (e) { setNote("The viewpoint's elements could not be isolated"); }
  }, [isolated, uids, store]);

  return { note, geometryNote, uids, isolated, toggleIsolation };
}
